import { Registration, EventSettings } from '../types';

/**
 * Gera o comprovante de inscrição como imagem PNG (Blob), desenhado em canvas.
 * Usado para compartilhar no WhatsApp com anexo (Web Share API) ou baixar.
 */
export async function generateReceiptImage(reg: Registration, settings: EventSettings): Promise<Blob> {
  try { await (document as any).fonts?.ready; } catch (e) {}

  const W = 1080;
  const PAD = 72;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d')!;

  const DISPLAY = "'Oswald', 'Arial Narrow', Arial, sans-serif";
  const SANS = "'Inter', Arial, sans-serif";

  const colors = {
    night: '#0B1510', dark: '#14261C', amber: '#D98A2B', ember: '#B4501E',
    sand: '#F4EFE6', paper: '#FBF9F4', stone: '#6B6F66', text: '#1C1917', green: '#2F6B45'
  };

  // Quebra de linha simples
  const wrap = (text: string, maxWidth: number, font: string): string[] => {
    ctx.font = font;
    const words = text.split(' ');
    const lines: string[] = [];
    let line = '';
    for (const w of words) {
      const test = line ? `${line} ${w}` : w;
      if (ctx.measureText(test).width > maxWidth && line) { lines.push(line); line = w; }
      else line = test;
    }
    if (line) lines.push(line);
    return lines;
  };

  const checklist = settings.checklist || [];
  const checklistCols = 2;
  const checklistRows = Math.ceil(checklist.length / checklistCols);

  // Altura calculada: cabeçalho + dados + bloco local + checklist + rodapé
  const H = 620 + 360 + 60 + checklistRows * 44 + 220;
  canvas.width = W;
  canvas.height = H;

  // Fundo
  ctx.fillStyle = colors.paper;
  ctx.fillRect(0, 0, W, H);

  // Cabeçalho escuro
  const headerH = 300;
  ctx.fillStyle = colors.dark;
  ctx.fillRect(0, 0, W, headerH);
  ctx.fillStyle = colors.amber;
  ctx.fillRect(0, headerH - 8, W, 8);

  ctx.textAlign = 'center';
  ctx.fillStyle = colors.amber;
  ctx.font = `700 22px ${SANS}`;
  ctx.fillText(`${settings.eventName}  •  ${settings.eventEdition}`.toUpperCase(), W / 2, 92);

  ctx.fillStyle = '#FFFFFF';
  ctx.font = `600 74px ${DISPLAY}`;
  ctx.fillText('COMPROVANTE DE INSCRIÇÃO', W / 2, 180);

  ctx.fillStyle = 'rgba(255,255,255,0.6)';
  ctx.font = `400 26px ${SANS}`;
  ctx.fillText(`${settings.eventDate} de ${settings.eventYear}  •  ${settings.retreatName}`, W / 2, 236);

  // Nome do inscrito
  let y = headerH + 90;
  ctx.fillStyle = colors.stone;
  ctx.font = `700 20px ${SANS}`;
  ctx.fillText('INSCRITO', W / 2, y);
  y += 62;
  ctx.fillStyle = colors.dark;
  const nameLines = wrap(reg.fullName.toUpperCase(), W - PAD * 2, `600 60px ${DISPLAY}`);
  ctx.font = `600 60px ${DISPLAY}`;
  for (const l of nameLines) { ctx.fillText(l, W / 2, y); y += 66; }

  // Linha tracejada
  y += 10;
  ctx.strokeStyle = '#E3DCCB';
  ctx.setLineDash([10, 10]);
  ctx.lineWidth = 2;
  ctx.beginPath(); ctx.moveTo(PAD, y); ctx.lineTo(W - PAD, y); ctx.stroke();
  ctx.setLineDash([]);

  // Grade de dados (2 colunas x 2 linhas)
  y += 60;
  const colW = (W - PAD * 2) / 2;
  const cell = (label: string, value: string, col: number, row: number, valueColor = colors.text, mono = false) => {
    const x = PAD + col * colW;
    const cy = y + row * 120;
    ctx.textAlign = 'left';
    ctx.fillStyle = colors.stone;
    ctx.font = `700 18px ${SANS}`;
    ctx.fillText(label.toUpperCase(), x, cy);
    ctx.fillStyle = valueColor;
    ctx.font = mono ? `700 34px 'Courier New', monospace` : `700 30px ${SANS}`;
    ctx.fillText(value, x, cy + 44);
  };
  cell('Inscrição #', reg.id?.slice(-8).toUpperCase() || '---', 0, 0, colors.ember, true);
  cell('Status', reg.status === 'paid' ? 'CONFIRMADA' : 'AGUARDANDO PAGAMENTO', 1, 0, reg.status === 'paid' ? colors.green : colors.ember);
  cell('Data do evento', `${settings.eventDate} de ${settings.eventYear}`, 0, 1);
  cell('Chegada', settings.eventTime || '', 1, 1);
  y += 240;

  // Bloco de locais
  ctx.fillStyle = colors.sand;
  roundRect(ctx, PAD, y, W - PAD * 2, 200, 28);
  ctx.fill();
  ctx.textAlign = 'left';
  let by = y + 50;
  ctx.fillStyle = colors.dark; ctx.font = `700 24px ${SANS}`;
  ctx.fillText('Chegada (sexta) e hospedagem', PAD + 36, by);
  ctx.fillStyle = colors.stone; ctx.font = `400 22px ${SANS}`;
  ctx.fillText(settings.retreatName + (settings.retreatAddress ? ` — ${settings.retreatAddress}` : ''), PAD + 36, by + 34);
  by += 96;
  ctx.fillStyle = colors.dark; ctx.font = `700 24px ${SANS}`;
  ctx.fillText('Culto de encerramento (domingo)', PAD + 36, by);
  ctx.fillStyle = colors.stone; ctx.font = `400 22px ${SANS}`;
  ctx.fillText(`${settings.locationName} — ${settings.locationAddress}`, PAD + 36, by + 34);
  y += 260;

  // Checklist
  ctx.fillStyle = colors.dark;
  ctx.font = `600 40px ${DISPLAY}`;
  ctx.fillText('O QUE LEVAR', PAD, y);
  y += 50;
  ctx.font = `400 22px ${SANS}`;
  checklist.forEach((item, i) => {
    const col = i % checklistCols;
    const row = Math.floor(i / checklistCols);
    const x = PAD + col * colW;
    const cy = y + row * 44;
    ctx.fillStyle = colors.green;
    ctx.fillText('✓', x, cy);
    ctx.fillStyle = colors.text;
    const short = item.length > 38 ? item.slice(0, 36) + '…' : item;
    ctx.fillText(short, x + 32, cy);
  });
  y += checklistRows * 44 + 60;

  // Rodapé
  ctx.textAlign = 'center';
  ctx.fillStyle = colors.stone;
  ctx.font = `400 20px ${SANS}`;
  ctx.fillText('Apresente este comprovante no check-in do evento.', W / 2, H - 70);
  ctx.fillStyle = colors.amber;
  ctx.font = `700 18px ${SANS}`;
  ctx.fillText('IGREJA PRESBITERIANA CENTRAL DE CASCAVEL', W / 2, H - 36);

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(b => b ? resolve(b) : reject(new Error('Falha ao gerar imagem')), 'image/png');
  });
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}
